import { useSystemTypes } from '@airship-platform/airship-patterns';
import { FlexBox } from '@airship-platform/jam-web/components';
import { CloseIcon } from '@airship-platform/jam-web/icons';
import Box, { BoxProps } from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { z } from 'zod';
import { useProducts } from '../../../../data-access/products/ProductsProvider';

const customSearchTerms = [
  'Full System',
  'Furnace',
  'Hybrid Full System',
  'Heat Pump',
  'Air Conditioner',
  'AC',
  'Mini Split',
  'Floor Mounted',
  'Wall Mounted',
  'Single Zone Condenser',
  'Multi Zone Condenser',
  'Ducted Concealed Unit',
];

export const ProductsFilterInputSchema = z.object({ searchTerm: z.string() });

export type ProductsFilterInputInputs = z.infer<
  typeof ProductsFilterInputSchema
>;

const ProductsFilterInput: React.FC<BoxProps> = ({ ...props }) => {
  const { control, watch, setValue } =
    useFormContext<ProductsFilterInputInputs>();
  const { availableBrands } = useProducts();
  const { metrics } = useSystemTypes();
  const ctrlRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [containerOptions, setContainerOptions] = useState({
    display: 'none',
    top: 0,
  });
  const [selectedTerms, setSelectedTerms] = useState<string[]>([]);

  const searchTerm = watch('searchTerm');

  useEffect(() => {
    const inputContainer = ctrlRef.current as HTMLDivElement;
    const input = inputRef.current as HTMLInputElement;
    const dropdown = dropdownRef.current as HTMLDivElement;
    if (inputContainer && input && dropdown) {
      const updateTerms = (searchTerm: string) =>
        setSelectedTerms((terms) =>
          terms.filter((term) => searchTerm.includes(`"${term}"`))
        );

      const onInputFocus = () =>
        window.requestAnimationFrame(() => {
          updateTerms(input.value);
          setContainerOptions((d) => ({
            ...d,
            top: inputContainer.getBoundingClientRect().height,
            display: 'block',
          }));
        });

      const onInputKeyUp = () => updateTerms(input.value);

      const onBackgroundClick = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        if (
          !inputContainer.contains(target) &&
          !input.contains(target) &&
          !dropdown.contains(target)
        ) {
          setContainerOptions((d) => ({ ...d, display: 'none' }));
        }
      };

      input.addEventListener('focus', onInputFocus);
      input.addEventListener('keyup', onInputKeyUp);
      document.addEventListener('click', onBackgroundClick);

      return () => {
        input.removeEventListener('focus', onInputFocus);
        input.removeEventListener('keyup', onInputKeyUp);
        document.removeEventListener('click', onBackgroundClick);
      };
    }
  }, []);

  const onItemClick = (value: string) => {
    const newTerm = `"${value}"`;
    if (searchTerm.includes(newTerm)) {
      setValue('searchTerm', searchTerm.replace(newTerm, '').trim());
    } else {
      setValue('searchTerm', `${searchTerm} ${newTerm}`.trim());
    }

    setSelectedTerms((terms) =>
      terms.includes(value)
        ? terms.filter((term) => term !== value)
        : [...terms, value]
    );
  };

  const renderList = (title: string, options: string[]) =>
    !!options.length && (
      <FlexBox flexDirection={'column'} gap={1}>
        <Typography>{title}</Typography>
        <FlexBox alignItems={'center'} gap={1} flexWrap={'wrap'}>
          {options.map((option) => (
            <Box
              key={option}
              onClick={() => onItemClick(option)}
              sx={{
                border: (theme) => `1px solid ${theme.palette.gray?.[300]}`,
                borderRadius: 2,
                px: 2,
                py: 1,
                backgroundColor: !selectedTerms.includes(option)
                  ? 'common.white'
                  : 'gold.light',
                cursor: 'pointer',
              }}
            >
              {option}
            </Box>
          ))}
        </FlexBox>
      </FlexBox>
    );

  return (
    <Box
      {...props}
      sx={[
        { position: 'relative' },
        ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
      ]}
    >
      <Controller
        name="searchTerm"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            placeholder={'Search by brand, equipment, or model'}
            fullWidth
            ref={ctrlRef}
            inputRef={inputRef}
            slotProps={{
              input: {
                endAdornment: field.value ? (
                  <IconButton
                    size="small"
                    aria-label="Clear search"
                    onClick={() => field.onChange('')}
                    edge="end"
                  >
                    <CloseIcon width="1rem" height="1rem" />
                  </IconButton>
                ) : null,
              },
            }}
          />
        )}
      />

      <Box
        sx={{
          position: 'absolute',
          display: containerOptions.display,
          top: containerOptions.top + 8,
          right: 0,
          left: 0,
          p: 3,
          zIndex: 99,
          backgroundColor: 'common.white',
          borderRadius: 2,
          boxShadow: (theme) => theme.shadows[2],
          border: (theme) => `1px solid ${theme.palette.gray?.[300]}`,
        }}
        ref={dropdownRef}
      >
        <FlexBox flexDirection={'column'} gap={3}>
          {renderList(
            'Brands',
            availableBrands
              .sort((a, b) => b.count - a.count)
              .slice(0, 5)
              .map((brand) => brand.name)
              .sort((a, b) => a.localeCompare(b))
          )}
          {renderList('Cooling Capacity', metrics.load.cooling)}
          {renderList('Heating Capacity', metrics.load.heating)}
          {renderList('System', customSearchTerms)}
        </FlexBox>
      </Box>
    </Box>
  );
};

export default ProductsFilterInput;
